/**
 * Transfer Code Utilities
 * 
 * Handles generation of transfer codes for web-to-app state transfer.
 * Transfer codes allow users to seamlessly move from web to native app
 * with their current chart state preserved.
 * 
 * Flow:
 * 1. Web app generates transfer code and stores chart state in Buffet backend
 * 2. Deep link includes transfer code: chordbaker://transfer?code=ABC123
 * 3. Native app retrieves chart state using transfer code
 * 4. Backend deletes transfer code after retrieval (one-time use)
 */

import { getRowSlots } from '@chordbaker/music-theory';
import { toWireSong } from './songSchema';
import { ChordCakeState } from '../types';

const BUFFET_API_URL = import.meta.env.VITE_BUFFET_API_URL || 'https://buffet.chordbaker.com/api';

export interface TransferCodeResponse {
  transferCode: string;
  expiresAt: string;
}

/**
 * Generate a transfer code for the current chart state
 * 
 * @param chartData - Current chart state to transfer
 * @returns Transfer code and expiration time
 * @throws Error if API request fails
 */
export async function generateTransferCode(chartData: ChordCakeState): Promise<TransferCodeResponse> {
  try {
    const response = await fetch(`${BUFFET_API_URL}/transfer/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        // The wire schema version, for the app versions in the field
        chartData: toWireSong(chartData),
        expiresIn: 600, // 10 minutes
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Failed to generate transfer code: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to generate transfer code:', error);
    throw error;
  }
}

/**
 * Build a deep link URL with transfer code
 * 
 * @param transferCode - Transfer code from backend
 * @returns Deep link URL for native app
 */
export function buildTransferDeepLink(transferCode: string): string {
  return `chordbaker://transfer?code=${transferCode}`;
}

/**
 * Check if chart state is empty (no content to transfer)
 * 
 * @param chartData - Chart state to check
 * @returns True if chart has no meaningful content
 */
export function isChartEmpty(chartData: ChordCakeState): boolean {
  // Check if title is empty
  const hasTitle = chartData.title && chartData.title.trim() !== '';
  
  // Check if any chords exist
  const hasChords = chartData.sections?.some(section =>
    section.rows?.some(row =>
      getRowSlots(row).some(slot => slot.chord !== null || slot.customText)
    )
  );

  return !hasTitle && !hasChords;
}
